import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { Col, Row } from "react-grid-system";
import {
  GetAccountResponse,
  GetPhoneOptionsResponse,
  GetPhonesResponse,
  PhoneContact,
} from "../../lib/api/models";
import {
  useGetPhoneOptions,
  useGetPhones,
  useVerifyPhones,
} from "../../lib/api/queries";
import { ToolType } from "../AccountDetails";
import { toolTypeToName } from "../AccountDetails/utils";
import StyledButton from "../common/buttons/StyledButton";
import Container from "../common/Container";
import Spinner from "../common/Spinner";
import StyledError from "../common/StyledError";
import AddPhoneNumber from "./AddPhoneNumber";
import EditPhoneNumber from "./EditPhoneNumber";
import PhoneNumberTable from "./PhoneNumberTable";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./Phones.module.css";
import ViewPhoneNumber from "./ViewPhoneNumber";

const strings = {
  close: "Close",
  errorTitle: "Failed to load Phones Requirements",
  activeNumbers: "ACTIVE NUMBERS",
  inactiveNumbers: "INACTIVE NUMBERS",
  verifySelected: "Verify Selected",
  emptyInactiveTableMessage:
    "There are no inactive telephone numbers for this card member",
  emptyActiveTableMessage:
    "There are no active telephone numbers for this card member",
  addNumber: "Add Number",
};

type PhonesToolStatus =
  | {
      status: "DEFAULT";
      currentPhoneContact: null;
    }
  | {
      status: "VIEW";
      currentPhoneContact: string;
    }
  | {
      status: "EDIT";
      currentPhoneContact: string;
    }
  | {
      status: "ADD";
      currentPhoneContact: null;
    };

type LoadingErrorContentProps = {
  onRequestClose: () => void;
} & (
  | {
      isLoading: true;
      isError?: undefined;
      error?: undefined;
      refetch?: undefined;
    }
  | {
      isLoading?: undefined;
      isError: true;
      error: unknown;
      refetch: () => void;
    }
);

function LoadingErrorContent({
  isLoading,
  isError,
  onRequestClose,
  error,
  refetch,
}: LoadingErrorContentProps) {
  if (!isLoading) {
    return null;
  }
  return (
    <>
      <Row className={toolStyles.header}>
        <Col className={toolStyles.centerCol} md={11}>
          <h2 className={toolStyles.title}>
            {toolTypeToName(ToolType.Phones)}
          </h2>
        </Col>
        <Col md={1}>
          <StyledButton
            className={toolStyles.headerButton}
            variant="secondary"
            onClick={onRequestClose}
          >
            {strings.close}
          </StyledButton>
        </Col>
      </Row>
      {isLoading ? (
        <Row>
          <Col>
            <Spinner />
          </Col>
        </Row>
      ) : isError ? (
        <Row>
          <Col>
            <StyledError
              errorTitle={strings.errorTitle}
              error={error}
              refetch={refetch}
            />
          </Col>
        </Row>
      ) : null}
    </>
  );
}

type PhonesContentProps = {
  account: GetAccountResponse;
  onRequestClose: () => void;
  setStatus: React.Dispatch<React.SetStateAction<PhonesToolStatus>>;
  currStatus: PhonesToolStatus;
  phoneData: GetPhonesResponse;
  phoneOptions: GetPhoneOptionsResponse;
  primaryNumber: string;
  secondaryNumber: string;
  referenceId?: string;
  onDisplayGeneralErrors: (err: unknown) => void;
  setToolInProgress: () => void;
};

function PhonesContent({
  account,
  onRequestClose,
  currStatus,
  setStatus,
  phoneData,
  phoneOptions,
  primaryNumber,
  secondaryNumber,
  referenceId,
  onDisplayGeneralErrors,
  setToolInProgress,
}: PhonesContentProps) {
  const { mutateAsync: verifyPhones, isLoading: isVerifyPhonesLoading } =
    useVerifyPhones(account.currentDwbuid);

  const [selectedToVerify, setSelectedToVerify] = useState<string[]>([]);

  const { activePhoneNumbers, inactivePhoneNumbers } = useMemo(() => {
    const activeNums: PhoneContact[] = [];
    const inactiveNums: PhoneContact[] = [];

    if (phoneData) {
      phoneData.phoneContacts.forEach((pc) =>
        pc.inactivation ? inactiveNums.push(pc) : activeNums.push(pc),
      );
    }
    return {
      activePhoneNumbers: activeNums,
      inactivePhoneNumbers: inactiveNums,
    };
  }, [phoneData]);

  const recordOfPhoneContacts = useMemo(() => {
    const map: Record<string, PhoneContact> = {};

    if (phoneData) {
      phoneData.phoneContacts.forEach((pc) => (map[pc.phoneID] = pc));
    }

    return map;
  }, [phoneData]);

  const recordOfExistingPhoneContactsByPhoneNumber = useMemo(() => {
    const map: Record<string, PhoneContact> = {};

    if (phoneData) {
      phoneData.phoneContacts.forEach(
        (pc) => (map[`${pc.phoneNumber}-${pc.extension}`] = pc),
      );
    }

    return map;
  }, [phoneData]);

  const isVerifyDisabled =
    activePhoneNumbers.length === 0 && inactivePhoneNumbers.length === 0;

  const onClickVerifyPhone = (id: string) =>
    setSelectedToVerify((prev) => {
      if (prev.includes(id)) {
        return prev.filter((v) => v !== id);
      }
      return [...prev, id];
    });

  const onClickView = (phoneContact: PhoneContact) =>
    setStatus({
      status: "VIEW",
      currentPhoneContact: phoneContact.phoneID,
    });

  const onClickEdit = (phoneContact: PhoneContact) =>
    setStatus({
      status: "EDIT",
      currentPhoneContact: phoneContact.phoneID,
    });

  const onClickCancel = () =>
    setStatus({
      status: "DEFAULT",
      currentPhoneContact: null,
    });

  const onSubmitVerifyPhones = (phoneIDs: string[]) => {
    return verifyPhones({
      partyId: account.partyId,
      phoneIDs,
      referenceId,
    }).catch(onDisplayGeneralErrors);
  };

  switch (currStatus.status) {
    case "DEFAULT":
      return (
        <>
          <Row className={toolStyles.header}>
            <Col className={toolStyles.centerCol} md={2}>
              <h2 className={toolStyles.title}>
                {toolTypeToName(ToolType.Phones)}
              </h2>
            </Col>
            <Col offset={{ md: 7 }} md={2}>
              <StyledButton
                className={toolStyles.headerButton}
                onClick={() =>
                  setStatus({
                    status: "ADD",
                    currentPhoneContact: null,
                  })
                }
              >
                {strings.addNumber}
              </StyledButton>
            </Col>
            <Col md={1}>
              <StyledButton
                className={toolStyles.headerButton}
                variant="secondary"
                onClick={onRequestClose}
              >
                {strings.close}
              </StyledButton>
            </Col>
          </Row>
          <Row>
            <Col>
              <PhoneNumberTable
                options={phoneOptions}
                headerText={strings.activeNumbers}
                headerContent={
                  <StyledButton
                    onClick={() => {
                      if (selectedToVerify.length < 1) {
                        return;
                      }
                      onSubmitVerifyPhones(selectedToVerify).then(() =>
                        setSelectedToVerify([]),
                      );
                    }}
                    isLoading={isVerifyPhonesLoading}
                    disabled={isVerifyDisabled || selectedToVerify.length < 1}
                  >
                    {strings.verifySelected}
                  </StyledButton>
                }
                headerClassName={styles.greenBackground}
                data={activePhoneNumbers}
                selectedToVerify={selectedToVerify}
                onClickVerify={onClickVerifyPhone}
                onClickView={onClickView}
                onClickEdit={onClickEdit}
                emptyTableMessage={strings.emptyActiveTableMessage}
                className={styles.phoneNumberTable}
                showInactivationReason={false}
              />
              <PhoneNumberTable
                options={phoneOptions}
                headerText={strings.inactiveNumbers}
                headerClassName={styles.yellowBackground}
                data={inactivePhoneNumbers}
                selectedToVerify={selectedToVerify}
                onClickVerify={onClickVerifyPhone}
                onClickView={onClickView}
                onClickEdit={onClickEdit}
                emptyTableMessage={strings.emptyInactiveTableMessage}
                className={styles.phoneNumberTable}
                showInactivationReason={true}
              />
              <div className={styles.inactiveVerifyButtonContainer}>
                <StyledButton
                  className={styles.inactiveVerifyButton}
                  onClick={() => {
                    if (selectedToVerify.length < 1) {
                      return;
                    }
                    onSubmitVerifyPhones(selectedToVerify).then(() =>
                      setSelectedToVerify([]),
                    );
                  }}
                  isLoading={isVerifyPhonesLoading}
                  disabled={isVerifyDisabled || selectedToVerify.length < 1}
                >
                  {strings.verifySelected}
                </StyledButton>
              </div>
            </Col>
          </Row>
        </>
      );
    case "VIEW":
      return (
        <ViewPhoneNumber
          phoneContact={recordOfPhoneContacts[currStatus.currentPhoneContact]}
          options={phoneOptions}
          onClickClose={onRequestClose}
          onClickEdit={onClickEdit}
          onClickCancel={onClickCancel}
          onVerify={(phoneId) => onSubmitVerifyPhones([phoneId])}
          isVerifyLoading={isVerifyPhonesLoading}
        />
      );
    case "EDIT":
      return (
        <EditPhoneNumber
          account={account}
          phoneContact={recordOfPhoneContacts[currStatus.currentPhoneContact]}
          options={phoneOptions}
          primaryNumber={primaryNumber}
          secondaryNumber={secondaryNumber}
          onClickCancel={onClickCancel}
          onClickClose={onRequestClose}
          onVerify={(phoneId) => onSubmitVerifyPhones([phoneId])}
          isVerifyLoading={isVerifyPhonesLoading}
          setToolInProgress={setToolInProgress}
          onDisplayGeneralErrors={onDisplayGeneralErrors}
          referenceId={referenceId}
        />
      );
    case "ADD":
      return (
        <AddPhoneNumber
          account={account}
          recordOfExistingPhoneContactsByPhoneNumber={
            recordOfExistingPhoneContactsByPhoneNumber
          }
          options={phoneOptions}
          primaryNumber={primaryNumber}
          secondaryNumber={secondaryNumber}
          onClickCancel={onClickCancel}
          onClickClose={onRequestClose}
          onClickEdit={onClickEdit}
          onClickView={onClickView}
          setToolInProgress={setToolInProgress}
          onDisplayGeneralErrors={onDisplayGeneralErrors}
          referenceId={referenceId}
        />
      );
  }
}

type Props = {
  account: GetAccountResponse;
  className?: string;
  toolId: string;
  onRequestClose: () => void;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  onDisplayGeneralErrors: (err: unknown) => void;
  referenceId?: string;
};

export default function Phones({
  account,
  toolId,
  onRequestClose,
  onSetToolInProgress,
  className,
  onDisplayGeneralErrors,
  referenceId,
}: Props) {
  const [status, setStatus] = useState<PhonesToolStatus>({
    status: "DEFAULT",
    currentPhoneContact: null,
  });

  const {
    data: getPhonesResponse,
    isLoading: isGetPhonesResponseLoading,
    isError: isGetPhonesResponseError,
    error: getPhonesResponseError,
    refetch: getPhonesResponseRefetch,
  } = useGetPhones(account.currentDwbuid);

  const {
    data: getPhoneOptionsResponse,
    isLoading: isGetPhoneOptionsResponseLoading,
    isError: isGetPhoneOptionsResponseError,
    error: getPhoneOptionsResponseError,
    refetch: getPhoneOptionsResponseRefetch,
  } = useGetPhoneOptions();

  const setToolInProgress = useCallback(() => {
    onSetToolInProgress(toolId, true);
  }, [onSetToolInProgress, toolId]);

  return (
    <div className={classNames(toolStyles.container, className)} id={toolId}>
      <Container>
        {isGetPhonesResponseLoading || isGetPhoneOptionsResponseLoading ? (
          <LoadingErrorContent isLoading onRequestClose={onRequestClose} />
        ) : isGetPhonesResponseError ? (
          <LoadingErrorContent
            isError
            error={getPhonesResponseError}
            refetch={getPhonesResponseRefetch}
            onRequestClose={onRequestClose}
          />
        ) : isGetPhoneOptionsResponseError ? (
          <LoadingErrorContent
            isError
            error={getPhoneOptionsResponseError}
            refetch={getPhoneOptionsResponseRefetch}
            onRequestClose={onRequestClose}
          />
        ) : (
          <PhonesContent
            account={account}
            onRequestClose={onRequestClose}
            setStatus={setStatus}
            currStatus={status}
            phoneData={getPhonesResponse}
            phoneOptions={getPhoneOptionsResponse}
            primaryNumber={getPhonesResponse.primaryPhone}
            secondaryNumber={getPhonesResponse.secondaryPhone}
            referenceId={referenceId}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            setToolInProgress={setToolInProgress}
          />
        )}
      </Container>
    </div>
  );
}
